import { TypedFormFieldComponents } from '@fl/cmsch-fe-library';
import React, { useMemo, FC, memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Opt } from 'ts-opt';
import { Accordion } from 'api/gen/Accordion';
import { AssociationType } from 'api/gen/AssociationType';
import { CriteriaDefinition } from 'api/gen/CriteriaDefinition';
import { OrganizationToChoose } from 'api/gen/OrganizationToChoose';
import { useForm } from 'utils/forms';
import { MatingBullsFilterFormValues, matingBullsFilterFormName, matingBullsInitialFormValues, MatingCowsFilterFormValues } from '../../types';
import { BottomButtons } from '../animals-filter/bottom-buttons';
import { AnimalsFormContainer } from '../animals-form-containter';
import { CriteriasInCollapse } from '../criteria-collapse';
import { ExtraFirstAccordionNodes } from './extra-first-accordion-nodes';
import { validate } from './validate';
import { matingAction } from '../../model';
const MALE = 'MALE';
const antFormProps = {
  layout: 'horizontal',
  labelAlign: 'left',
  labelCol: {
    xs: {
      span: 24
    },
    sm: {
      span: 8
    }
  },
  wrapperCol: {
    xs: {
      span: 24
    },
    sm: {
      span: 16
    }
  },
  labelWrap: true
} as const;
interface Props {
  /** Association type used by the filter form. */
  associationType: AssociationType;
  /** Saved organizations available for the selector. */
  organizations: Opt<Array<OrganizationToChoose>>;
  /** Accordion definitions shown in the filter form. */
  criteriaAccordions: Opt<Array<Accordion>>;
  /** Criteria definitions shown in the filter form. */
  criteriaDefinitions: Opt<Array<CriteriaDefinition>>;
  /** Whether no saved criteria lists exist. */
  criteriaListsEmpty: boolean;
  /** Whether no saved animal lists exist. */
  animalListsEmpty: boolean;
  /** Whether the animals page is currently empty. */
  isAnimalsPageEmpty: boolean;
  /** Whether the organizations selector is loading. */
  organizationsLoading: boolean;
  /** Handles form submission. */
  onSubmit(): void;
  /** Opens or closes the saved animal lists modal. */
  toggleAnimalListsVisibility(): void;
  /** Opens or closes the add-animal modal. */
  toggleAddAnimalsVisibility(): void;
  /** Opens or closes the add-from-DB modal. */
  toggleAddAnimalsFromDBVisibility(): void;
  /** Opens or closes the overview modal for saved lists. */
  toggleListsOverviewVisibility(): void;
  /** Opens or closes the saved criteria lists modal. */
  toggleCriteriaListsVisibility(): void;
  /** Opens or closes the criteria-list-details modal. */
  toggleCriteriaListDetailsVisibility(): void;
}
const MatingBullsFilterFormBase: FC<Props> = props => {
  const {
    associationType,
    organizations,
    criteriaAccordions,
    criteriaDefinitions,
    criteriaListsEmpty,
    animalListsEmpty,
    isAnimalsPageEmpty,
    organizationsLoading,
    onSubmit,
    toggleAnimalListsVisibility,
    toggleAddAnimalsVisibility,
    toggleAddAnimalsFromDBVisibility,
    toggleListsOverviewVisibility,
    toggleCriteriaListsVisibility,
    toggleCriteriaListDetailsVisibility
  } = props;
  const dispatch = useDispatch();
  const resetBullsAccordionValues = useCallback((accordionId: number): void => {
    dispatch(matingAction.resetAccordionValues(MALE, accordionId));
  }, [dispatch]);
  const resetBullsCriteriaValue = useCallback((criteriaCode: Opt<string>): void => {
    dispatch(matingAction.resetSingleCriteriaValue(MALE, criteriaCode.orElse('')));
  }, [dispatch]);
  const {
    Form,
    Fields,
    valid,
    submitting
  } = useForm({
    form: matingBullsFilterFormName,
    initialValues: matingBullsInitialFormValues,
    onSubmit,
    validate
  }, antFormProps);
  const extraFirstAccordionNodes = useMemo(() => <ExtraFirstAccordionNodes organizations={organizations} organizationsLoading={organizationsLoading} Fields={Fields} />, [Fields, organizations, organizationsLoading]);
  const criteriaAccordionNodes = useMemo(() => <CriteriasInCollapse criteriaAccordions={criteriaAccordions.orElse([])} criteriaDefinitions={criteriaDefinitions.orElse([])} extraFirstAccordionNodes={extraFirstAccordionNodes} resetCowsAccordionValues={resetBullsAccordionValues} resetCriteriaValue={resetBullsCriteriaValue}
  // TODO: improve type after updating typescript to 4.7+
  Fields={Fields as TypedFormFieldComponents<MatingCowsFilterFormValues | MatingBullsFilterFormValues>} />, [Fields, criteriaAccordions, criteriaDefinitions, extraFirstAccordionNodes, resetBullsAccordionValues, resetBullsCriteriaValue]);
  const bottomButtons = useMemo(() => <BottomButtons sex={MALE} associationType={associationType} valid={valid} criteriaListsEmpty={criteriaListsEmpty} animalListsEmpty={animalListsEmpty} isAnimalsPageEmpty={isAnimalsPageEmpty} submitting={submitting} toggleAnimalListsVisibility={toggleAnimalListsVisibility} toggleAddAnimalsVisibility={toggleAddAnimalsVisibility} toggleAddAnimalsFromDBVisibility={toggleAddAnimalsFromDBVisibility} toggleListsOverviewVisibility={toggleListsOverviewVisibility} toggleCriteriaListsVisibility={toggleCriteriaListsVisibility} toggleCriteriaListDetailsVisibility={toggleCriteriaListDetailsVisibility} largerBottomSpacing />, [associationType, valid, submitting, criteriaListsEmpty, animalListsEmpty, isAnimalsPageEmpty, toggleAnimalListsVisibility, toggleAddAnimalsVisibility, toggleAddAnimalsFromDBVisibility, toggleListsOverviewVisibility, toggleCriteriaListsVisibility, toggleCriteriaListDetailsVisibility]);
  return useMemo(() => <AnimalsFormContainer>
            <Form>
                {criteriaAccordionNodes}
                {bottomButtons}
            </Form>
        </AnimalsFormContainer>, [Form, bottomButtons, criteriaAccordionNodes]);
};

/**
 * Memoized bull filter form used by the mating workflow.
 *
 * @param props Filter state and modal toggles.
 * @returns The bull filter form.
 */
export const MatingBullsFilterForm = memo(MatingBullsFilterFormBase);